import { CommunityContract } from 'types/contracts';
import { FeeDataType } from 'types/fees';

import { LivingInfoDataType, LivingInfoGroupedDataType, LivingInfoStatsType } from './types';
import { isNonNegativeNumber, toNullableNumber } from '../../../utils/helpers/numbers';
import { FEES_LEVELS_FIELDS } from '../../ShopData/constants';

export const getLevelsAverage = (data: FeeDataType | undefined) => {
  if (data) {
    const levelKeys = Object.values(FEES_LEVELS_FIELDS);
    const { levelsSum, levelsCount } = (levelKeys as Array<keyof FeeDataType>).reduce(
      (acc, level) => {
        const value = data[level] as number;
        if (isNonNegativeNumber(value)) {
          acc.levelsSum += value;
          acc.levelsCount += 1;
        }
        return acc;
      },
      { levelsSum: 0, levelsCount: 0 }
    );

    if (levelsCount > 0) {
      return levelsSum / levelsCount;
    }
  }
};

export const groupLivingInfoByApartmentType = (data: LivingInfoDataType[]): LivingInfoGroupedDataType[] => {
  const grouped: Record<number, LivingInfoGroupedDataType> = {};
  const OTHER_GROUP_ID = -1;

  data.forEach((item) => {
    const group = item.apartment_type_groups[0];

    const groupId = group ? group.id : OTHER_GROUP_ID;
    const groupName = group ? group.name : 'Other';

    if (!grouped[groupId]) {
      grouped[groupId] = {
        group_name: groupName,
        group_id: groupId,
        apartments: [],
      };
    }

    grouped[groupId].apartments.push(item);
  });

  return Object.values(grouped);
};

export const formatNumber = (value: any) => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : value.toFixed(2);
  }
  return value;
};

export const formatNumberWithCommas = (number: number): string => {
  return new Intl.NumberFormat('en-US').format(number);
};

const getNumberStats = (values: Array<number | null>) => {
  const validValues = values.filter((value): value is number => typeof value === 'number' && value >= 0);

  if (!validValues.length) {
    return {
      min: 0,
      avg: 0,
      max: 0,
    };
  }

  const total = validValues.reduce((sum, value) => sum + value, 0);

  return {
    min: Math.min(...validValues),
    avg: parseFloat((total / validValues.length).toFixed(1)),
    max: Math.max(...validValues),
  };
};

export const calculateLivingInfoStats = (data: LivingInfoDataType[]): LivingInfoStatsType => {
  let minBaseRentItem: LivingInfoDataType | null = null;
  let maxBaseRentItem: LivingInfoDataType | null = null;
  let totalBaseRent = 0;
  let totalItemsWithBaseRent = 0;

  data.forEach((item) => {
    const baseRent = toNullableNumber(item.base_rent);

    if (typeof baseRent !== 'number' || baseRent < 0) {
      return;
    }

    totalBaseRent += baseRent;
    totalItemsWithBaseRent++;

    if (!minBaseRentItem || baseRent < Number(minBaseRentItem.base_rent)) {
      minBaseRentItem = item;
    }

    if (!maxBaseRentItem || baseRent > Number(maxBaseRentItem.base_rent)) {
      maxBaseRentItem = item;
    }
  });

  const avgBaseRent = totalItemsWithBaseRent > 0 ? parseFloat((totalBaseRent / totalItemsWithBaseRent).toFixed(1)) : 0;
  const sqFootageStats = getNumberStats(data.map((item) => toNullableNumber(item.sq_footage)));

  return {
    minBaseRentItem,
    avgBaseRent,
    maxBaseRentItem,
    minSqFootage: sqFootageStats.min,
    avgSqFootage: sqFootageStats.avg,
    maxSqFootage: sqFootageStats.max,
  };
};

export const sortCommunityContracts = (a: CommunityContract, b: CommunityContract) => {
  if (a.name < b.name) return -1;
  if (a.name > b.name) return 1;
  // names are equal, sort by refund_percentage
  return a.refund_percentage! - b.refund_percentage!;
};
